import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { CheckCircle, Clock, Lock, Play, Award, Gift, ArrowRight, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const BootCampDashboard = () => {
  const [bootcampData, setBootcampData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);

  useEffect(() => {
    fetchBootcampData();
  }, []);

  const fetchBootcampData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_BASE_URL}/api/bootcamp/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        setBootcampData(response.data);
      }
    } catch (error) {
      console.error('Error fetching bootcamp data:', error);
      toast.error('Failed to load boot camp data');
    } finally {
      setLoading(false);
    }
  };

  const completeItem = async (itemId) => {
    setCompleting(itemId);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_BASE_URL}/api/bootcamp/complete/${itemId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        toast.success(response.data.message);
        if (response.data.points > 0) {
          toast.success(`Earned ${response.data.points} points!`, { icon: '🎉' });
        }
        if (response.data.credits > 0) {
          toast.success(`Earned ${response.data.credits} ad credits!`, { icon: '💰' });
        }
        await fetchBootcampData(); // Refresh data
      }
    } catch (error) {
      console.error('Error completing item:', error);
      toast.error('Failed to complete item');
    } finally {
      setCompleting(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'available':
      case 'in_progress':
        return <Play className="w-5 h-5 text-blue-500" />;
      case 'locked':
        return <Lock className="w-5 h-5 text-gray-400" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'available':
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'locked':
        return 'bg-gray-100 text-gray-500 border-gray-200';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!bootcampData) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Boot Camp Not Available</h2>
        <p className="text-gray-600">Unable to load your boot camp data. Please try again later.</p>
      </div>
    );
  }

  const { progress, checklist, notifications } = bootcampData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-green-600 p-6 rounded-lg text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">🎓 BBC Quick List Boot Camp</h1>
            <p className="text-lg opacity-90">
              {progress.completion_percentage < 100 
                ? "Master the fundamentals and unlock Phase 2!" 
                : "🎉 Congratulations! You've completed Boot Camp!"}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{Math.round(progress.completion_percentage)}%</div>
            <div className="text-sm opacity-90">Complete</div>
          </div>
        </div>
        
        <div className="mt-4">
          <Progress 
            value={progress.completion_percentage} 
            className="h-3 bg-white/20"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div>
                <div className="text-2xl font-bold text-green-600">{progress.items_completed}</div>
                <div className="text-sm text-gray-600">Completed</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Award className="w-5 h-5 text-blue-500" />
              <div>
                <div className="text-2xl font-bold text-blue-600">{progress.total_points}</div>
                <div className="text-sm text-gray-600">Points Earned</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Gift className="w-5 h-5 text-orange-500" />
              <div>
                <div className="text-2xl font-bold text-orange-600">{progress.total_credits_earned}</div>
                <div className="text-sm text-gray-600">Ad Credits</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Zap className="w-5 h-5 text-purple-500" />
              <div>
                <div className="text-2xl font-bold text-purple-600">{progress.login_streak}</div>
                <div className="text-sm text-gray-600">Day Streak</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Phase 2 Preview (if ready) */}
      {progress.ready_for_phase_2 && (
        <Card className="border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 to-orange-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">🚀 Phase 2 Unlocked!</h3>
                <p className="text-gray-700">
                  Congratulations! You've mastered the fundamentals. Advanced tools and the 12x3 matrix await!
                </p>
              </div>
              <Button className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-500 hover:to-orange-600">
                Explore Phase 2 <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span>📋 Boot Camp Checklist</span>
            <Badge variant="outline">
              {progress.items_completed} / {progress.items_total}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {checklist.map((item, index) => (
            <div 
              key={item.id} 
              className={`p-4 rounded-lg border-2 transition-all ${getStatusColor(item.status)}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(item.status)}
                  <div>
                    <h4 className="font-semibold">{item.title}</h4>
                    <p className="text-sm opacity-80">{item.description}</p>
                    {item.credits_reward > 0 && (
                      <div className="flex items-center space-x-2 mt-1">
                        <Gift className="w-4 h-4" />
                        <span className="text-xs font-medium">
                          {item.points} points + {item.credits_reward} credits
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Badge className={`text-xs ${getStatusColor(item.status)}`}>
                    {item.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                  
                  {(item.status === 'available' || item.status === 'in_progress') && (
                    <Button
                      size="sm"
                      onClick={() => completeItem(item.id)}
                      disabled={completing === item.id}
                      className="bg-yellow-400 text-black hover:bg-yellow-500"
                    >
                      {completing === item.id ? 'Completing...' : 'Complete'}
                    </Button>
                  )}
                </div>
              </div>
              
              {item.status === 'available' && (
                <div className="mt-3 text-sm bg-white/50 p-3 rounded border-l-4 border-blue-500">
                  <strong>Instructions:</strong> {item.instructions}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Recent Notifications */}
      {notifications && notifications.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>🔔 Recent Updates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {notifications.slice(0, 3).map((notif) => (
              <div key={notif.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div>
                  <h5 className="font-medium">{notif.title}</h5>
                  <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                  <div className="text-xs text-gray-500 mt-2">
                    {new Date(notif.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BootCampDashboard;